import React from 'react';
import PropTypes from 'prop-types';
import {Provider} from 'react-redux';
import {ConnectedRouter} from 'connected-react-router';
import {ErrorBoundary as SentryErrorBoundary} from '@sentry/react';

import {ThemeProvider} from '@spothero/ui';

import history from 'utils/history';
import store from 'store/store';

import App from 'common/app/App';
import {AutoScrollProvider} from 'hooks/auto-scroll';
import ErrorDisplay from 'common/errors/error-display/ErrorDisplay';
import AuthProviderWithHistory from 'common/auth/AuthProviderWithHistory';

import theme from 'common/theme';
import {AnimationProvider} from 'common/animation/AnimationContext';
import {FacilityMultiSelectProvider} from 'common/facility-sidebar/context/FacilityMultisSelectContext';

const FallbackComponent = ({error, componentStack, resetError}) => (
    <ErrorDisplay
        error={error}
        componentStack={componentStack}
        resetError={resetError}
    />
);

FallbackComponent.propTypes = {
    error: PropTypes.instanceOf(Error).isRequired,
    componentStack: PropTypes.string.isRequired,
    resetError: PropTypes.func.isRequired,
};

const Root = () => {
    return (
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <AuthProviderWithHistory history={history}>
                    <ThemeProvider useLegacyOverrides theme={theme}>
                        <SentryErrorBoundary
                            fallback={FallbackComponent}
                            beforeCapture={(scope) => {
                                scope.setTag('path', 'Root');
                            }}
                        >
                            <AutoScrollProvider>
                                <AnimationProvider>
                                    <FacilityMultiSelectProvider>
                                        <App />
                                    </FacilityMultiSelectProvider>
                                </AnimationProvider>
                            </AutoScrollProvider>
                        </SentryErrorBoundary>
                    </ThemeProvider>
                </AuthProviderWithHistory>
            </ConnectedRouter>
        </Provider>
    );
};

export default Root;
